/**
 * 即時合成音效引擎（Web Audio API，無需外部音檔）
 * 所有 SFX 透過 Oscillator + 包絡 (envelope) 即時產生。
 */

let ctx: AudioContext | null = null;
let masterGain: GainNode | null = null;
let muted = localStorage.getItem('audioMuted') === '1';
let volume = parseFloat(localStorage.getItem('audioVolume') ?? '0.5');

function ensureCtx(): AudioContext | null {
  if (ctx) return ctx;
  try {
    const C = (window.AudioContext || (window as any).webkitAudioContext) as typeof AudioContext;
    ctx = new C();
    masterGain = ctx.createGain();
    masterGain.gain.value = muted ? 0 : volume;
    masterGain.connect(ctx.destination);
    // iOS 解鎖：第一次 user gesture 觸發
    const unlock = () => {
      ctx?.resume();
      document.removeEventListener('pointerdown', unlock);
      document.removeEventListener('keydown', unlock);
    };
    document.addEventListener('pointerdown', unlock, { once: true });
    document.addEventListener('keydown', unlock, { once: true });
    return ctx;
  } catch {
    return null;
  }
}

export function setMuted(m: boolean) {
  muted = m;
  localStorage.setItem('audioMuted', m ? '1' : '0');
  if (masterGain) masterGain.gain.value = m ? 0 : volume;
}
export function isMuted() { return muted; }
export function setVolume(v: number) {
  volume = Math.max(0, Math.min(1, v));
  localStorage.setItem('audioVolume', String(volume));
  if (masterGain && !muted) masterGain.gain.value = volume;
}
export function getVolume() { return volume; }

interface ToneOpts {
  freq: number;            // 基礎頻率 Hz
  freqEnd?: number;        // 結束頻率（滑音）
  type?: OscillatorType;   // 波形
  duration: number;        // 秒
  attack?: number;         // 起音時間
  release?: number;        // 釋放時間
  gain?: number;           // 音量（0-1）
  detune?: number;
  delay?: number;          // 延遲多少秒後開始（用於做 chord/arp）
}
function tone(o: ToneOpts) {
  const c = ensureCtx();
  if (!c || !masterGain) return;
  const t0 = c.currentTime + (o.delay ?? 0);
  const osc = c.createOscillator();
  osc.type = o.type ?? 'sine';
  osc.frequency.setValueAtTime(o.freq, t0);
  if (o.freqEnd != null) osc.frequency.exponentialRampToValueAtTime(Math.max(1, o.freqEnd), t0 + o.duration);
  if (o.detune) osc.detune.setValueAtTime(o.detune, t0);
  const g = c.createGain();
  const peak = (o.gain ?? 0.5) * 0.6;
  const att = o.attack ?? 0.005;
  const rel = o.release ?? Math.min(0.15, o.duration);
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(peak, t0 + att);
  g.gain.linearRampToValueAtTime(peak * 0.7, t0 + Math.max(att, o.duration - rel));
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + o.duration);
  osc.connect(g).connect(masterGain);
  osc.start(t0);
  osc.stop(t0 + o.duration + 0.05);
}

/**
 * 木質敲擊合成：以多個非諧波（inharmonic）共振峰 + 極短噪音瞬態 + 對數衰減
 * 模擬真實麻將牌 / 拉密牌 的觸碰聲。
 */
function woodKnock(o: {
  delay?: number;
  /** 主共振頻率（Hz）— pick 用較高（1500–2200），place 用較低（500–900）。 */
  freq: number;
  /** 整體音量 */
  gain?: number;
  /** 衰減時間 */
  decay?: number;
  /** 起音瞬態強度 */
  click?: number;
}) {
  const c = ensureCtx();
  if (!c || !masterGain) return;
  const t0 = c.currentTime + (o.delay ?? 0);
  const dur = o.decay ?? 0.08;
  const peak = (o.gain ?? 0.5) * 0.6;
  const click = (o.click ?? 0.4) * 0.6;

  // ---- 1. 起音瞬態：極短高通噪音（模擬硬物接觸的「咔」聲）----
  const nLen = Math.max(1, Math.floor(c.sampleRate * 0.012));
  const nBuf = c.createBuffer(1, nLen, c.sampleRate);
  const nData = nBuf.getChannelData(0);
  for (let i = 0; i < nLen; i++) nData[i] = (Math.random() * 2 - 1) * (1 - i / nLen);
  const nSrc = c.createBufferSource();
  nSrc.buffer = nBuf;
  const nFilter = c.createBiquadFilter();
  nFilter.type = 'highpass';
  nFilter.frequency.value = Math.max(2000, o.freq * 1.5);
  const nGain = c.createGain();
  nGain.gain.setValueAtTime(click, t0);
  nGain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.018);
  nSrc.connect(nFilter).connect(nGain).connect(masterGain);
  nSrc.start(t0);
  nSrc.stop(t0 + 0.025);

  // ---- 2. 多共振峰（非諧波關係，模擬木材模態）----
  // 真實木材常見比例：1.0, 1.6, 2.7, 4.1
  const partials = [
    { f: o.freq, g: 1.0, d: dur },
    { f: o.freq * 1.6, g: 0.55, d: dur * 0.7 },
    { f: o.freq * 2.7, g: 0.3, d: dur * 0.5 },
    { f: o.freq * 4.1, g: 0.15, d: dur * 0.35 },
  ];
  for (const p of partials) {
    const osc = c.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(p.f, t0);
    // 微小頻率下降（敲擊瞬間頻率會略降）
    osc.frequency.exponentialRampToValueAtTime(p.f * 0.95, t0 + p.d);
    const g = c.createGain();
    g.gain.setValueAtTime(0, t0);
    g.gain.linearRampToValueAtTime(peak * p.g, t0 + 0.001);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + p.d);
    osc.connect(g).connect(masterGain);
    osc.start(t0);
    osc.stop(t0 + p.d + 0.02);
  }
}

function noise(o: { duration: number; gain?: number; lpf?: number; hpf?: number; delay?: number }) {
  const c = ensureCtx();
  if (!c || !masterGain) return;
  const t0 = c.currentTime + (o.delay ?? 0);
  const len = Math.max(1, Math.floor(c.sampleRate * o.duration));
  const buf = c.createBuffer(1, len, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  const src = c.createBufferSource();
  src.buffer = buf;
  let last: AudioNode = src;
  if (o.lpf) {
    const f = c.createBiquadFilter();
    f.type = 'lowpass'; f.frequency.value = o.lpf;
    last.connect(f); last = f;
  }
  if (o.hpf) {
    const f = c.createBiquadFilter();
    f.type = 'highpass'; f.frequency.value = o.hpf;
    last.connect(f); last = f;
  }
  const g = c.createGain();
  const peak = (o.gain ?? 0.3) * 0.6;
  g.gain.setValueAtTime(peak, t0);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + o.duration);
  last.connect(g).connect(masterGain);
  src.start(t0);
  src.stop(t0 + o.duration + 0.05);
}

// ===== 音效定義 =====

export const sfx = {
  // 互動：木質敲擊聲
  tilePick: () => woodKnock({ freq: 1800, gain: 0.45, decay: 0.06, click: 0.5 }),
  tilePlace: () => woodKnock({ freq: 700, gain: 0.6, decay: 0.12, click: 0.7 }),
  tileInvalid: () => {
    // 重物悶響 + 低頻嗡鳴
    woodKnock({ freq: 280, gain: 0.4, decay: 0.18, click: 0.3 });
    tone({ freq: 110, type: 'sawtooth', duration: 0.22, gain: 0.18, attack: 0.005, delay: 0.04 });
  },
  tileSwap: () => woodKnock({ freq: 1400, gain: 0.3, decay: 0.05, click: 0.3 }),
  tileGroupPick: () => {
    // 多張同時被抬起（兩聲輕快敲擊）
    woodKnock({ freq: 900, gain: 0.5, decay: 0.1, click: 0.5 });
    woodKnock({ freq: 1300, gain: 0.4, decay: 0.08, click: 0.4, delay: 0.04 });
  },

  // 遊戲事件
  meldSuccess: () => {
    // C-E-G 上行
    [523, 659, 784].forEach((f, i) => tone({ freq: f, type: 'triangle', duration: 0.18, gain: 0.5, delay: i * 0.06 }));
  },
  initialMeld: () => {
    // C-E-G-C 上升 + 鈴
    [523, 659, 784, 1047].forEach((f, i) => tone({ freq: f, type: 'triangle', duration: 0.25, gain: 0.5, delay: i * 0.08 }));
    [523, 1047].forEach((f, i) => tone({ freq: f * 2, type: 'sine', duration: 0.5, gain: 0.2, delay: 0.32 + i * 0.04 }));
  },
  jokerSwap: () => {
    // 魔法上升音
    tone({ freq: 600, freqEnd: 1800, type: 'triangle', duration: 0.4, gain: 0.4 });
    tone({ freq: 900, freqEnd: 2400, type: 'sine', duration: 0.4, gain: 0.25, delay: 0.04 });
  },
  jokerAppear: () => {
    [659, 880, 1175].forEach((f, i) => tone({ freq: f, type: 'sine', duration: 0.3, gain: 0.35, delay: i * 0.05 }));
  },
  drawTile: () => {
    noise({ duration: 0.18, gain: 0.25, lpf: 3000, hpf: 500 });
  },
  pass: () => tone({ freq: 280, freqEnd: 200, type: 'sine', duration: 0.3, gain: 0.3, release: 0.2 }),
  undo: () => tone({ freq: 800, freqEnd: 400, type: 'triangle', duration: 0.18, gain: 0.4 }),
  resetTurn: () => {
    tone({ freq: 800, freqEnd: 300, type: 'triangle', duration: 0.4, gain: 0.45 });
    tone({ freq: 600, freqEnd: 200, type: 'sine', duration: 0.4, gain: 0.25, delay: 0.05 });
  },

  // 回合
  turnStart: () => {
    tone({ freq: 880, type: 'sine', duration: 0.15, gain: 0.4 });
    tone({ freq: 1318, type: 'sine', duration: 0.15, gain: 0.3, delay: 0.08 });
  },
  turnOther: () => tone({ freq: 440, type: 'sine', duration: 0.08, gain: 0.25 }),
  turnWarning: () => tone({ freq: 700, type: 'square', duration: 0.06, gain: 0.3, attack: 0.001, release: 0.05 }),
  turnTimeout: () => {
    tone({ freq: 200, freqEnd: 100, type: 'sawtooth', duration: 0.6, gain: 0.5 });
    noise({ duration: 0.15, gain: 0.3, lpf: 600 });
  },

  // 房間
  matchFound: () => {
    [523, 659, 784, 1047].forEach((f, i) => tone({ freq: f, type: 'sine', duration: 0.15, gain: 0.4, delay: i * 0.04 }));
  },
  roomCreate: () => {
    [523, 784, 1047].forEach((f, i) => tone({ freq: f, type: 'triangle', duration: 0.2, gain: 0.4, delay: i * 0.06 }));
  },
  playerJoin: () => tone({ freq: 880, freqEnd: 1175, type: 'sine', duration: 0.18, gain: 0.35 }),
  playerLeave: () => tone({ freq: 587, freqEnd: 392, type: 'sine', duration: 0.2, gain: 0.3 }),

  // 結算
  gameWin: () => {
    // 勝利旋律 C-E-G-C-E-G-C
    const notes = [523, 659, 784, 1047, 1319, 1568, 2093];
    notes.forEach((f, i) => tone({ freq: f, type: 'triangle', duration: 0.2, gain: 0.45, delay: i * 0.08 }));
    tone({ freq: 523, type: 'sine', duration: 1.5, gain: 0.25, delay: 0.6 });
    tone({ freq: 784, type: 'sine', duration: 1.5, gain: 0.2, delay: 0.6 });
    tone({ freq: 1047, type: 'sine', duration: 1.5, gain: 0.18, delay: 0.6 });
  },
  gameLose: () => {
    // 下降小調
    [392, 311, 262].forEach((f, i) => tone({ freq: f, type: 'triangle', duration: 0.4, gain: 0.4, delay: i * 0.18 }));
  },
  gameDraw: () => {
    [523, 659, 523].forEach((f, i) => tone({ freq: f, type: 'sine', duration: 0.3, gain: 0.35, delay: i * 0.15 }));
  },
  tileClear: () => {
    [659, 880, 1047, 1319, 1760].forEach((f, i) => tone({ freq: f, type: 'triangle', duration: 0.25, gain: 0.5, delay: i * 0.06 }));
  },

  // 表情
  emote: () => {
    tone({ freq: 1200, freqEnd: 1500, type: 'sine', duration: 0.1, gain: 0.4 });
    tone({ freq: 800, freqEnd: 600, type: 'sine', duration: 0.08, gain: 0.25, delay: 0.05 });
  },
};

export type SfxKey = keyof typeof sfx;
export function play(key: SfxKey) { sfx[key](); }
